import { useState, useMemo } from "react";

const B = "#45a9c5";
const D = "#105776";
const BL = "#d6eff6";
const BLL = "#eef8fb";
const fmt = n => "$" + Math.abs(Math.round(n)).toLocaleString();
const num = n => Math.round(n).toLocaleString();
const lc = s => s.toLowerCase();

function TermSelect({ label, value, onChange, options, helper }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: D, marginBottom: 4 }}>{label}</label>
      {helper && <p style={{ fontSize: 12, color: "#4a7a8a", margin: "0 0 5px", lineHeight: 1.5 }}>{helper}</p>}
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ width: "100%", padding: "9px 12px", fontSize: 14, border: "1.5px solid #b8dde8", borderRadius: 8, background: "#fff", color: D, outline: "none" }}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function Field({ label, value, onChange, suffix, prefix, helper, min = 0, max, step = 1, small }) {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState("");
  const displayVal = value === "" ? "" : focused ? raw : (typeof value === "number" ? value.toLocaleString() : value) + (suffix && !focused ? " " + suffix : "");
  return (
    <div style={{ marginBottom: small ? 10 : 16 }}>
      <label style={{ display: "block", fontSize: small ? 12 : 13, fontWeight: 500, color: D, marginBottom: 3 }}>{label}</label>
      {helper && <p style={{ fontSize: 12, color: "#4a7a8a", margin: "0 0 5px", lineHeight: 1.5 }}>{helper}</p>}
      <div style={{ display: "flex", alignItems: "center", border: `1.5px solid ${focused ? B : "#b8dde8"}`, borderRadius: 8, background: "#fff", overflow: "hidden" }}>
        {prefix && <span style={{ padding: "0 8px", fontSize: small ? 12 : 14, color: D, background: BLL, borderRight: "1px solid #b8dde8", whiteSpace: "nowrap" }}>{prefix}</span>}
        <input type="text" value={focused ? raw : displayVal} placeholder="0"
          onFocus={() => { setRaw(value === "" ? "" : value); setFocused(true); }}
          onBlur={e => { setFocused(false); const v = parseFloat(e.target.value); if (e.target.value === "" || isNaN(v)) { onChange(""); return; } onChange(max !== undefined ? Math.min(max, Math.max(min, v)) : Math.max(min, v)); }}
          onChange={e => setRaw(e.target.value)}
          style={{ flex: 1, border: "none", outline: "none", padding: small ? "6px 10px" : "9px 12px", fontSize: small ? 13 : 14, color: D, background: "transparent", minWidth: 0 }} />
        {suffix && <span style={{ padding: "0 8px", fontSize: small ? 11 : 13, color: B, background: BLL, borderLeft: "1px solid #b8dde8", whiteSpace: "nowrap" }}>{suffix}</span>}
      </div>
    </div>
  );
}

function CalcRow({ label, before, after, highlight }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, padding: "7px 10px", borderRadius: 6, background: highlight ? BLL : "transparent", marginBottom: 2, alignItems: "center" }}>
      <span style={{ fontSize: 13, color: highlight ? D : "#4a7a8a" }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: highlight ? 500 : 400, color: D, textAlign: "right" }}>{before}</span>
      <span style={{ fontSize: 13, fontWeight: highlight ? 500 : 400, color: highlight ? B : D, textAlign: "right" }}>{after}</span>
    </div>
  );
}

function CalcTable({ rows }) {
  return (
    <div style={{ border: `1px solid ${BL}`, borderRadius: 10, overflow: "hidden", marginTop: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, padding: "7px 10px", background: D }}>
        <span style={{ fontSize: 11, fontWeight: 500, color: "#a8d8e8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Item</span>
        <span style={{ fontSize: 11, fontWeight: 500, color: "#a8d8e8", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "right" }}>Current situation</span>
        <span style={{ fontSize: 11, fontWeight: 500, color: BL, textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "right" }}>With degree planning platform in place</span>
      </div>
      {rows.map((row, i) => <CalcRow key={i} {...row} />)}
    </div>
  );
}

function Explainer({ children }) {
  return (
    <div style={{ fontSize: 13, color: "#4a7a8a", lineHeight: 1.7, margin: "0 0 20px", padding: "12px 14px", background: BLL, borderLeft: `3px solid ${B}`, borderRadius: "0 8px 8px 0" }}>
      {children}
    </div>
  );
}

function SummaryRow({ label, value, highlight }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, padding: "7px 10px", borderRadius: 6, background: highlight ? BLL : "transparent", marginBottom: 2, alignItems: "center" }}>
      <span style={{ fontSize: 13, fontWeight: highlight ? 500 : 400, color: highlight ? D : "#4a7a8a" }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: highlight ? 500 : 400, color: D }}>{value}</span>
    </div>
  );
}

function ReportTable({ rows, highlightRow }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 8 }}>
      <thead>
        <tr style={{ background: D }}>
          <th style={{ padding: "6px 10px", textAlign: "left", color: "#a8d8e8", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" }}>Item</th>
          <th style={{ padding: "6px 10px", textAlign: "right", color: "#a8d8e8", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" }}>Current situation</th>
          <th style={{ padding: "6px 10px", textAlign: "right", color: BL, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" }}>With degree planning platform in place</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([l, b, a], i) => (
          <tr key={i} style={{ borderBottom: `1px solid ${BL}` }}>
            <td style={{ padding: "6px 10px", color: "#4a7a8a" }}>{l}</td>
            <td style={{ padding: "6px 10px", color: D, textAlign: "right" }}>{b}</td>
            <td style={{ padding: "6px 10px", color: D, textAlign: "right" }}>{a}</td>
          </tr>
        ))}
        <tr style={{ background: BLL }}>
          <td style={{ padding: "6px 10px", fontWeight: 600, color: D }}>{highlightRow[0]}</td>
          <td style={{ padding: "6px 10px", fontWeight: 600, color: D, textAlign: "right" }}>{highlightRow[1]}</td>
          <td style={{ padding: "6px 10px", fontWeight: 600, color: B, textAlign: "right" }}>{highlightRow[2]}</td>
        </tr>
      </tbody>
    </table>
  );
}

function ReportNote({ children }) {
  return (
    <div style={{ fontSize: 12, color: "#4a7a8a", lineHeight: 1.6, margin: "0 0 10px", padding: "9px 12px", background: "#f7fcfe", borderLeft: `3px solid ${B}`, borderRadius: "0 6px 6px 0" }}>{children}</div>
  );
}

function CalcBox({ label, children }) {
  return (
    <div style={{ padding: "10px 12px", border: `0.5px solid ${BL}`, borderRadius: 8, marginBottom: 20 }}>
      <p style={{ fontSize: 11, fontWeight: 500, color: D, margin: "0 0 4px" }}>{label}</p>
      <p style={{ fontSize: 11, color: "#4a7a8a", margin: 0 }}>{children}</p>
    </div>
  );
}

function ReportSection({ title }) {
  return (
    <p style={{ fontSize: 13, fontWeight: 500, color: D, margin: "0 0 8px", paddingBottom: 5, borderBottom: `2px solid ${B}` }}>{title}</p>
  );
}

export default function App() {
  const [step, setStep] = useState(0);
  const [progTerm, setProgTerm] = useState("Program");
  const [subjTerm, setSubjTerm] = useState("Subject");
  const p = progTerm; const pl = lc(p); const s = subjTerm; const sl = lc(s);

  const [eftsl, setEftsl] = useState(20000);
  const [commencing, setCommencing] = useState(7000);
  const [pricePerEftsl, setPricePerEftsl] = useState("");
  const [implCost, setImplCost] = useState(0);
  const [req1B, setReq1B] = useState(55);
  const [req2B, setReq2B] = useState(25);
  const [req1A, setReq1A] = useState(30);
  const [req2A, setReq2A] = useState(10);
  const [peakPct, setPeakPct] = useState(80);
  const [unmetPct, setUnmetPct] = useState(20);
  const [peakDays, setPeakDays] = useState(60);
  const [minsB, setMinsB] = useState(35);
  const [minsA, setMinsA] = useState(15);
  const [advisorSalary, setAdvisorSalary] = useState(90000);
  const [daysPerYear, setDaysPerYear] = useState(250);
  const [hoursPerDay, setHoursPerDay] = useState(7.25);
  const [capUtil, setCapUtil] = useState(85);
  const [attrB, setAttrB] = useState(12.0);
  const [attrA, setAttrA] = useState(10.0);
  const [revPerStudent, setRevPerStudent] = useState(19770);
  const [unrYears, setUnrYears] = useState(2);
  const [dropB, setDropB] = useState(5.0);
  const [dropA, setDropA] = useState(4.0);
  const [subjectEnrol, setSubjectEnrol] = useState(160000);
  const [revPerSubject, setRevPerSubject] = useState(2471);

  const r = useMemo(() => {
    const cap = (capUtil || 85) / 100;
    const hpd = hoursPerDay || 7.25;
    const dpy = daysPerYear || 250;
    const totalB = (req1B / 100 + req2B / 100) * eftsl;
    const totalA = (req1A / 100 + req2A / 100) * eftsl;
    const peakB = totalB * peakPct / 100;
    const peakA = totalA * peakPct / 100;
    const sessB = peakB * (1 - unmetPct / 100);
    const sessA = peakA * (1 - unmetPct / 100);
    const capB = (hpd * 60 * cap) / minsB;
    const capA = (hpd * 60 * cap) / minsA;
    const advisorsB = sessB / (capB * peakDays);
    const advisorsA = sessA / (capA * peakDays);
    const prorated = advisorSalary * (peakDays / dpy);
    const costB = advisorsB * prorated * 2;
    const costA = advisorsA * prorated * 2;
    const advisingSavings = costB - costA;
    const leavingB = commencing * attrB / 100;
    const leavingA = commencing * attrA / 100;
    const attrRevB = leavingB * unrYears * revPerStudent;
    const attrRevA = leavingA * unrYears * revPerStudent;
    const attrGain = attrRevB - attrRevA;
    const droppedB = subjectEnrol * dropB / 100;
    const droppedA = subjectEnrol * dropA / 100;
    const dropRevB = droppedB * revPerSubject;
    const dropRevA = droppedA * revPerSubject;
    const dropGain = dropRevB - dropRevA;
    const totalBenefit = advisingSavings + attrGain + dropGain;
    const subCost = eftsl * (parseFloat(pricePerEftsl) || 0);
    const totalCost = subCost + (implCost || 0);
    const roi = totalCost > 0 ? ((totalBenefit - totalCost) / totalCost) * 100 : 0;
    const bcRatio = totalCost > 0 ? totalBenefit / totalCost : 0;
    return { totalB, totalA, peakB, peakA, sessB, sessA, capB, capA, advisorsB, advisorsA, prorated, costB, costA, advisingSavings, leavingB, leavingA, attrRevB, attrRevA, attrGain, droppedB, droppedA, dropRevB, dropRevA, dropGain, totalBenefit, subCost, totalCost, netBenefit: totalBenefit - totalCost, roi, bcRatio };
  }, [eftsl, commencing, advisorSalary, pricePerEftsl, implCost, req1B, req2B, req1A, req2A, peakPct, unmetPct, peakDays, minsB, minsA, daysPerYear, hoursPerDay, capUtil, attrB, attrA, revPerStudent, unrYears, dropB, dropA, subjectEnrol, revPerSubject]);

  const STEPS = ["Institution", "Advising demand", "Advisor productivity", "Attrition", "Dropped " + sl + "s", "Results", "Summary report"];

  const eftslVal = eftsl;
  const commencingVal = commencing;

  const steps = [

    // STEP 0 — Institution
    <div key="s0">
      <Explainer>This tool estimates the financial benefit of implementing a degree planning platform at your university. Work through each section entering your own figures — every number can be adjusted to reflect your institution's context. The final step produces a business case report you can share internally.</Explainer>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, padding: "14px 16px", background: BLL, borderRadius: 10, marginBottom: 20 }}>
        <TermSelect label="What do you call a degree or qualification?" value={progTerm} onChange={setProgTerm} options={["Program", "Course", "Degree", "Qualification"]} helper="e.g. Bachelor of Science is a Program, Course, or Degree at your institution" />
        <TermSelect label="What do you call an individual unit of study?" value={subjTerm} onChange={setSubjTerm} options={["Subject", "Unit", "Course", "Module", "Paper"]} helper="e.g. Introduction to Statistics is a Subject, Unit, or Course at your institution" />
      </div>
      <Field label="Student load (EFTSL)" value={eftsl} onChange={setEftsl} min={500} helper="Your total equivalent full-time student load — the standard measure of undergraduate enrolment volume." />
      <Field label="Commencing bachelor students" value={commencing} onChange={setCommencing} min={100} helper="The number of new bachelor students enrolling for the first time each year. Used to calculate attrition-related revenue." />
    </div>,

    // STEP 1 — Advising demand
    <div key="s1">
      <Explainer>During peak enrolment periods, advising teams are under significant pressure. This section estimates total advising demand based on how many students typically seek help each semester, and how that demand would likely change if students had access to self-service degree planning tools.</Explainer>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div>
          <p style={{ fontSize: 12, fontWeight: 500, color: D, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>Current situation</p>
          <Field label="Students making 1 request / semester" value={req1B} onChange={setReq1B} suffix="%" min={0} max={100} />
          <Field label="Students making 2 requests / semester" value={req2B} onChange={setReq2B} suffix="%" min={0} max={100} />
          <Field label="% of requests during peak period" value={peakPct} onChange={setPeakPct} suffix="%" min={0} max={100} helper="Requests concentrated in enrolment periods." />
          <Field label="Acceptable % of peak requests unmet" value={unmetPct} onChange={setUnmetPct} suffix="%" min={0} max={100} helper="The proportion of peak demand that overflows — students who can't get an appointment." />
        </div>
        <div>
          <p style={{ fontSize: 12, fontWeight: 500, color: B, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>With degree planning platform in place</p>
          <Field label="Students making 1 request / semester" value={req1A} onChange={setReq1A} suffix="%" min={0} max={100} />
          <Field label="Students making 2 requests / semester" value={req2A} onChange={setReq2A} suffix="%" min={0} max={100} />
        </div>
      </div>
      <CalcTable rows={[
        { label: "Total requests / semester", before: num(r.totalB), after: num(r.totalA) },
        { label: "Requests during peak", before: num(r.peakB), after: num(r.peakA) },
        { label: "Sessions required at peak", before: num(r.sessB), after: num(r.sessA), highlight: true },
      ]} />
    </div>,

    // STEP 2 — Advisor productivity
    <div key="s2">
      <Explainer>A degree planning platform significantly increases advisor productivity by automating routine workflows — including version-specific program information, subject sequencing, and student plan management. This reduces the time required for each advisor to address student inquiries while maintaining service quality.</Explainer>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div>
          <p style={{ fontSize: 12, fontWeight: 500, color: D, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>Current situation</p>
          <Field label="Typical session duration" value={minsB} onChange={setMinsB} suffix="min" min={5} max={120} helper="Average time per advising appointment today." />
        </div>
        <div>
          <p style={{ fontSize: 12, fontWeight: 500, color: B, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>With degree planning platform in place</p>
          <Field label="Typical session duration" value={minsA} onChange={setMinsA} suffix="min" min={5} max={120} helper="Expected session time with platform in place." />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Duration of peak enrolment period" value={peakDays} onChange={setPeakDays} suffix="working days" min={10} max={200} helper="The number of working days in your busiest enrolment window each semester." />
        <Field label="Academic advisor annual salary" value={advisorSalary} onChange={setAdvisorSalary} prefix="$" min={40000} helper="Include superannuation, leave loading, and other on-costs." />
      </div>
      <CalcTable rows={[
        { label: "Requests handled per advisor per day", before: (Math.round(r.capB * 10) / 10).toString(), after: (Math.round(r.capA * 10) / 10).toString() },
        { label: "Advisors needed to meet demand at peak", before: (Math.round(r.advisorsB * 10) / 10).toString(), after: (Math.round(r.advisorsA * 10) / 10).toString() },
        { label: "Total annual advising cost", before: fmt(r.costB), after: fmt(r.costA), highlight: true },
        { label: "Annual cost saving", before: "—", after: fmt(r.advisingSavings), highlight: true },
      ]} />
      <div style={{ marginTop: 16, padding: "12px 14px", border: `0.5px solid ${BL}`, borderRadius: 8 }}>
        <p style={{ fontSize: 12, fontWeight: 500, color: D, margin: "0 0 8px" }}>How the annual advising cost is calculated</p>
        <p style={{ fontSize: 12, color: "#4a7a8a", lineHeight: 1.7, margin: 0 }}>Annual advising cost = advisors needed at peak × (annual salary × peak days ÷ working days per year) × 2 semesters</p>
        <p style={{ fontSize: 12, color: "#4a7a8a", lineHeight: 1.7, margin: "6px 0 0" }}>Before: <span style={{ color: D, fontWeight: 500 }}>{Math.round(r.advisorsB * 10) / 10} advisors</span> × (<span style={{ color: D, fontWeight: 500 }}>{fmt(advisorSalary)}</span> × <span style={{ color: D, fontWeight: 500 }}>{peakDays} days</span> ÷ <span style={{ color: D, fontWeight: 500 }}>{daysPerYear}</span>) × 2 = <span style={{ color: B, fontWeight: 500 }}>{fmt(r.costB)}</span></p>
        <p style={{ fontSize: 12, color: "#4a7a8a", lineHeight: 1.7, margin: "4px 0 0" }}>After: <span style={{ color: D, fontWeight: 500 }}>{Math.round(r.advisorsA * 10) / 10} advisors</span> × (<span style={{ color: D, fontWeight: 500 }}>{fmt(advisorSalary)}</span> × <span style={{ color: D, fontWeight: 500 }}>{peakDays} days</span> ÷ <span style={{ color: D, fontWeight: 500 }}>{daysPerYear}</span>) × 2 = <span style={{ color: B, fontWeight: 500 }}>{fmt(r.costA)}</span></p>
      </div>
      <div style={{ marginTop: 16, padding: "12px 14px", border: `0.5px solid ${BL}`, borderRadius: 8 }}>
        <p style={{ fontSize: 12, fontWeight: 500, color: D, margin: "0 0 10px" }}>Calculation assumptions</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Field label="Working days per year" value={daysPerYear} onChange={setDaysPerYear} min={200} max={300} small />
          <Field label="Hours worked per day" value={hoursPerDay} onChange={setHoursPerDay} min={4} max={10} step={0.25} small />
          <Field label="Capacity utilisation" value={capUtil} onChange={setCapUtil} suffix="%" min={50} max={100} small />
        </div>
        <p style={{ fontSize: 11, color: "#7ab8c8", margin: "6px 0 0", lineHeight: 1.5 }}>Capacity utilisation reflects the proportion of an advisor's time spent on advising, net of administrative tasks, meetings, and other duties.</p>
      </div>
    </div>,

    // STEP 3 — Attrition
    <div key="s3">
      <Explainer>First-year attrition represents a substantial and often underappreciated revenue risk. Each student who departs early takes with them multiple years of unrealised enrolment income. A degree planning platform reduces this risk by equipping students with the information they need to choose the right {pl} and {sl}s from the outset — addressing one of the root causes of early departure.</Explainer>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Attrition rate — current" value={attrB} onChange={setAttrB} suffix="%" min={0} max={50} step={0.1} helper={"The percentage of commencing students who leave their " + pl + " permanently at the end of first year."} />
        <Field label="Attrition rate — with degree planning platform" value={attrA} onChange={setAttrA} suffix="%" min={0} max={50} step={0.1} helper="Your estimate of the improved retention rate with the platform in place." />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Average student revenue per year" value={revPerStudent} onChange={setRevPerStudent} prefix="$" min={1000} helper="Average annual tuition revenue per student — use a blended rate if your domestic/international mix is significant." />
        <Field label={"Average remaining years of " + pl} value={unrYears} onChange={setUnrYears} suffix="years" min={1} max={6} helper={"How many years of study are lost when a first-year student leaves their " + pl + " early. A 3-year " + pl + " student leaving after year 1 represents 2 lost years."} />
      </div>
      <CalcTable rows={[
        { label: "Commencing bachelor students", before: num(commencing), after: num(commencing) },
        { label: "Students leaving at end of year 1", before: num(r.leavingB), after: num(r.leavingA) },
        { label: "Additional students retained", before: "—", after: num(r.leavingB - r.leavingA) },
        { label: "Annual revenue loss from first-year attrition", before: fmt(r.attrRevB), after: fmt(r.attrRevA) },
        { label: "Annual revenue gain", before: "—", after: fmt(r.attrGain), highlight: true },
      ]} />
      <div style={{ marginTop: 16, padding: "12px 14px", border: `0.5px solid ${BL}`, borderRadius: 8 }}>
        <p style={{ fontSize: 12, fontWeight: 500, color: D, margin: "0 0 8px" }}>How the revenue gain is calculated</p>
        <p style={{ fontSize: 12, color: "#4a7a8a", lineHeight: 1.7, margin: 0 }}>Annual revenue gain = students additionally retained × average annual student revenue × average remaining years of {pl}</p>
        <p style={{ fontSize: 12, color: "#4a7a8a", lineHeight: 1.7, margin: "6px 0 0" }}><span style={{ color: D, fontWeight: 500 }}>{num(r.leavingB - r.leavingA)} students</span> × <span style={{ color: D, fontWeight: 500 }}>{fmt(revPerStudent)}</span> × <span style={{ color: D, fontWeight: 500 }}>{unrYears} years</span> = <span style={{ color: B, fontWeight: 500 }}>{fmt(r.attrGain)}</span></p>
      </div>
    </div>,

    // STEP 4 — Dropped subjects
    <div key="s4">
      <Explainer>Each pre-census withdrawal represents tuition revenue that the university will not recover for that teaching period. Students typically withdraw because a {sl} doesn't align with their {pl} requirements, proves more demanding than expected, or because they were unaware of a more suitable option. A degree planning platform equips students with the information they need to make confident, well-matched {sl} selections before census.</Explainer>
      <Field label={"Total " + sl + " enrolments per year"} value={subjectEnrol} onChange={setSubjectEnrol} min={1000} helper={"The total number of individual " + sl + " enrolments across all bachelor students in a year. A useful estimate is EFTSL × 8 (two semesters × four " + sl + "s)."} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label={"Pre-census " + sl + " withdrawal rate — current"} value={dropB} onChange={setDropB} suffix="%" min={0} max={20} step={0.1} helper={"The percentage of " + sl + " enrolments withdrawn before the census date each year."} />
        <Field label={"Pre-census " + sl + " withdrawal rate — with platform"} value={dropA} onChange={setDropA} suffix="%" min={0} max={20} step={0.1} helper={"Your estimate of the improved " + sl + " withdrawal rate with the platform in place."} />
      </div>
      <Field label={"Average revenue per " + sl + " enrolment"} value={revPerSubject} onChange={setRevPerSubject} prefix="$" min={100} helper={"The average tuition fee for a single " + sl + " enrolment. Divide annual tuition revenue by total " + sl + " enrolments for a blended rate."} />
      <CalcTable rows={[
        { label: s + "s dropped before census", before: num(r.droppedB), after: num(r.droppedA) },
        { label: "Fewer " + sl + "s dropped", before: "—", after: num(r.droppedB - r.droppedA) },
        { label: "Revenue loss from dropped " + sl + "s", before: fmt(r.dropRevB), after: fmt(r.dropRevA) },
        { label: "Annual revenue gain", before: "—", after: fmt(r.dropGain), highlight: true },
      ]} />
      <div style={{ marginTop: 16, padding: "12px 14px", border: `0.5px solid ${BL}`, borderRadius: 8 }}>
        <p style={{ fontSize: 12, fontWeight: 500, color: D, margin: "0 0 8px" }}>How the revenue gain is calculated</p>
        <p style={{ fontSize: 12, color: "#4a7a8a", lineHeight: 1.7, margin: 0 }}>Annual revenue gain = fewer {sl}s dropped before census × average revenue per {sl} enrolment</p>
        <p style={{ fontSize: 12, color: "#4a7a8a", lineHeight: 1.7, margin: "6px 0 0" }}><span style={{ color: D, fontWeight: 500 }}>{num(r.droppedB - r.droppedA)} {sl}s</span> × <span style={{ color: D, fontWeight: 500 }}>{fmt(revPerSubject)}</span> = <span style={{ color: B, fontWeight: 500 }}>{fmt(r.dropGain)}</span></p>
      </div>
    </div>,

    // STEP 5 — Results
    <div key="s5">
      <Explainer>This summary combines all three benefit streams — advising cost savings, attrition revenue recovery, and reduced pre-census {sl} withdrawals — and weighs them against the cost of the platform. Enter your expected subscription and implementation costs below to see the net return.</Explainer>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <Field label="Subscription cost per EFTSL" value={pricePerEftsl} onChange={setPricePerEftsl} prefix="$" suffix="per EFTSL" min={1} step={0.01} helper={pricePerEftsl ? "Annual subscription total: " + fmt(r.subCost) : "Enter your quoted price per EFTSL"} />
        <Field label="Additional annual costs" value={implCost} onChange={setImplCost} prefix="$" min={0} helper="Include implementation, training, support, or integration costs spread across a year." />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Total annual benefit", value: fmt(r.totalBenefit), accent: true },
          { label: "Total annual cost", value: fmt(r.totalCost) },
          { label: "Return on investment", value: r.totalCost > 0 ? Math.round(r.roi) + "%" : "—" },
          { label: "Benefit / cost ratio", value: r.totalCost > 0 ? (Math.round(r.bcRatio * 10) / 10) + "x" : "—" },
        ].map((c, i) => (
          <div key={i} style={{ background: c.accent ? D : BLL, borderRadius: 10, padding: "14px 16px" }}>
            <p style={{ fontSize: 12, fontWeight: 500, color: c.accent ? "#a8d8e8" : "#4a7a8a", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.04em" }}>{c.label}</p>
            <p style={{ fontSize: 24, fontWeight: 500, color: c.accent ? "#fff" : D, margin: 0 }}>{c.value}</p>
          </div>
        ))}
      </div>
      <div style={{ border: `1px solid ${BL}`, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "7px 10px", background: D }}>
          <span style={{ fontSize: 11, fontWeight: 500, color: "#a8d8e8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Investment summary</span>
        </div>
        <SummaryRow label="Academic advising cost savings" value={fmt(r.advisingSavings)} />
        <SummaryRow label="Revenue gain — reduced attrition" value={fmt(r.attrGain)} />
        <SummaryRow label={"Revenue gain — fewer " + sl + "s dropped before census"} value={fmt(r.dropGain)} />
        <SummaryRow label="Total annual benefit" value={fmt(r.totalBenefit)} highlight />
        <SummaryRow label="Annual subscription cost" value={"−" + fmt(r.subCost)} />
        {implCost > 0 && <SummaryRow label="Additional annual costs" value={"−" + fmt(implCost)} />}
        <SummaryRow label="Net annual benefit" value={fmt(r.netBenefit)} highlight />
        <SummaryRow label="Annual return on investment (ROI = (Total benefit − Total cost) ÷ Total cost × 100)" value={r.totalCost > 0 ? Math.round(r.roi) + "%" : "—"} />
        <SummaryRow label="Benefit / cost ratio" value={r.totalCost > 0 ? (Math.round(r.bcRatio * 10) / 10) + "x" : "—"} />
      </div>
      <p style={{ fontSize: 11, color: "#7ab8c8", marginTop: 20, lineHeight: 1.6 }}>This analysis contains forward-looking estimates based on user-provided inputs and generalised assumptions. It is intended as an indicative planning tool only and does not constitute financial advice. Actual results will depend on a range of factors specific to your institution and implementation context.</p>
    </div>,

    // STEP 6 — Summary report
    <div key="s6">
      <style>{`@media print { .no-print { display: none !important; } .print-report { border: none !important; } }`}</style>
      <div className="no-print" style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <button onClick={() => window.print()} style={{ padding: "8px 20px", borderRadius: 8, border: `1.5px solid ${B}`, background: "none", color: B, fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
          Save as PDF
        </button>
      </div>
      <div style={{ borderBottom: `2px solid ${B}`, paddingBottom: 6, marginBottom: 16 }}>
        <p style={{ fontSize: 15, fontWeight: 500, color: D, margin: 0 }}>Degree planning platform — business case</p>
        <p style={{ fontSize: 12, color: "#4a7a8a", margin: "2px 0 0" }}>{new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}</p>
      </div>

      <div style={{ display: "flex", gap: 24, padding: "10px 14px", background: BLL, borderRadius: 8, marginBottom: 20 }}>
        <div>
          <span style={{ fontSize: 11, fontWeight: 500, color: "#4a7a8a", textTransform: "uppercase", letterSpacing: "0.04em" }}>Student load (EFTSL)</span>
          <span style={{ fontSize: 13, fontWeight: 500, color: D, marginLeft: 8 }}>{num(eftslVal)}</span>
        </div>
        <div style={{ borderLeft: `1px solid ${BL}`, paddingLeft: 24 }}>
          <span style={{ fontSize: 11, fontWeight: 500, color: "#4a7a8a", textTransform: "uppercase", letterSpacing: "0.04em" }}>Commencing bachelor students</span>
          <span style={{ fontSize: 13, fontWeight: 500, color: D, marginLeft: 8 }}>{num(commencingVal)}</span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 24 }}>
        {[
          { label: "Total annual benefit", value: fmt(r.totalBenefit), accent: true },
          { label: "Total annual cost", value: fmt(r.totalCost) },
          { label: "Annual ROI", value: r.totalCost > 0 ? Math.round(r.roi) + "%" : "—" },
          { label: "Benefit / cost ratio", value: r.totalCost > 0 ? (Math.round(r.bcRatio * 10) / 10) + "x" : "—" },
        ].map((c, i) => (
          <div key={i} style={{ background: c.accent ? D : BLL, borderRadius: 8, padding: "12px 14px" }}>
            <p style={{ fontSize: 11, fontWeight: 500, color: c.accent ? "#a8d8e8" : "#4a7a8a", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.04em" }}>{c.label}</p>
            <p style={{ fontSize: 18, fontWeight: 500, color: c.accent ? "#fff" : D, margin: 0 }}>{c.value}</p>
          </div>
        ))}
      </div>

      <ReportSection title="1. Academic advising cost savings" />
      <ReportNote>A degree planning platform significantly increases advisor productivity by automating routine workflows — including version-specific {pl} information, {sl} sequencing, and student plan management. This reduces the time required for each advisor to address student inquiries while maintaining service quality.</ReportNote>
      <ReportTable
        rows={[
          ["Advising requests per semester", num(r.totalB), num(r.totalA)],
          ["Sessions required at peak", num(r.sessB), num(r.sessA)],
          ["Average session duration", minsB + " min", minsA + " min"],
          ["Advisors needed at peak", (Math.round(r.advisorsB * 10) / 10).toString(), (Math.round(r.advisorsA * 10) / 10).toString()],
          ["Annual advising cost", fmt(r.costB), fmt(r.costA)],
        ]}
        highlightRow={["Annual advising cost savings", "—", fmt(r.advisingSavings)]}
      />
      <div style={{ fontSize: 11, color: "#7ab8c8", padding: "7px 10px", border: `1px solid ${BL}`, borderRadius: 6, marginBottom: 20 }}>
        Calculation assumptions — Working days per year: {daysPerYear} · Hours worked per day: {hoursPerDay} · Capacity utilisation: {capUtil}%
      </div>

      <ReportSection title="2. Revenue gain from reduced student attrition" />
      <ReportNote>First-year attrition represents a substantial and often underappreciated revenue risk. Each student who departs early takes with them multiple years of unrealised enrolment income. A degree planning platform reduces this risk by equipping students with the information they need to choose the right {pl} and {sl}s from the outset — addressing one of the root causes of early departure.</ReportNote>
      <ReportTable
        rows={[
          ["Commencing bachelor students", num(commencing), num(commencing)],
          ["First-year attrition rate", attrB + "%", attrA + "%"],
          ["Students leaving at end of year 1", num(r.leavingB), num(r.leavingA)],
          ["Additional students retained", "—", num(r.leavingB - r.leavingA)],
          ["Annual revenue loss from first-year attrition", fmt(r.attrRevB), fmt(r.attrRevA)],
        ]}
        highlightRow={["Annual revenue gain", "—", fmt(r.attrGain)]}
      />
      <CalcBox label="How the revenue gain is calculated">
        {num(r.leavingB - r.leavingA)} students × {fmt(revPerStudent)} × {unrYears} years = {fmt(r.attrGain)}
      </CalcBox>

      <ReportSection title={"3. Revenue gain from fewer " + sl + "s dropped before census"} />
      <ReportNote>Each pre-census withdrawal represents tuition revenue that the university will not recover for that teaching period. Students typically withdraw because a {sl} doesn't align with their {pl} requirements, proves more demanding than expected, or because they were unaware of a more suitable option. A degree planning platform equips students with the information they need to make confident, well-matched {sl} selections before census.</ReportNote>
      <ReportTable
        rows={[
          ["Total " + sl + " enrolments per year", num(subjectEnrol), num(subjectEnrol)],
          [s + "s dropped before census", num(r.droppedB) + " (" + dropB + "%)", num(r.droppedA) + " (" + dropA + "%)"],
          ["Revenue loss from dropped " + sl + "s", fmt(r.dropRevB), fmt(r.dropRevA)],
        ]}
        highlightRow={["Annual revenue gain", "—", fmt(r.dropGain)]}
      />
      <CalcBox label="How the revenue gain is calculated">
        {num(r.droppedB - r.droppedA)} {sl}s × {fmt(revPerSubject)} = {fmt(r.dropGain)}
      </CalcBox>

      <ReportSection title="Investment summary" />
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 16 }}>
        <tbody>
          {[
            ["Academic advising cost savings", fmt(r.advisingSavings), false],
            ["Revenue gain — reduced attrition", fmt(r.attrGain), false],
            ["Revenue gain — fewer " + sl + "s dropped before census", fmt(r.dropGain), false],
            ["Total annual financial benefit", fmt(r.totalBenefit), true],
            ["Annual subscription cost", "−" + fmt(r.subCost), false],
            ...(implCost > 0 ? [["Additional annual costs", "−" + fmt(implCost), false]] : []),
            ["Net annual benefit", fmt(r.netBenefit), true],
            ["Annual return on investment (ROI = (Total benefit − Total cost) ÷ Total cost × 100)", r.totalCost > 0 ? Math.round(r.roi) + "%" : "—", false],
            ["Benefit / cost ratio", r.totalCost > 0 ? (Math.round(r.bcRatio * 10) / 10) + "x" : "—", false],
          ].map(([label, value, hl], i) => (
            <tr key={i} style={{ background: hl ? BLL : "transparent", borderBottom: `1px solid ${BL}` }}>
              <td style={{ padding: "6px 10px", fontSize: 12, fontWeight: hl ? 500 : 400, color: hl ? D : "#4a7a8a" }}>{label}</td>
              <td style={{ padding: "6px 10px", fontSize: 12, fontWeight: hl ? 500 : 400, color: hl ? D : "#4a7a8a", textAlign: "right" }}>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p style={{ fontSize: 11, color: "#7ab8c8", lineHeight: 1.6, marginBottom: 16 }}>This analysis contains forward-looking estimates based on user-provided inputs and generalised assumptions. It is intended as an indicative planning tool only and does not constitute financial advice. Actual results will depend on a range of factors specific to your institution and implementation context.</p>
      <p style={{ fontSize: 13, color: "#4a7a8a", textAlign: "center", lineHeight: 1.6 }}>
        Interested in learning more? Reach out to our team at <a href="mailto:info@studyplanner.com.au" style={{ color: B, textDecoration: "none", fontWeight: 500 }}>info@studyplanner.com.au</a>
      </p>
    </div>,
  ];

  return (
    <div style={{ fontFamily: "var(--font-sans)", padding: "1rem 0 2rem" }}>
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: 22, fontWeight: 500, color: D, margin: "0 0 4px" }}>Degree planning software — business case calculator</p>
        <p style={{ fontSize: 13, color: "#4a7a8a", margin: 0 }}>Estimate the cost savings and revenue impact of implementing a degree planning platform at your institution</p>
        <div style={{ width: 48, height: 3, background: B, borderRadius: 2, marginTop: 10 }} />
      </div>
      <div style={{ display: "flex", gap: 0, marginBottom: 28, borderBottom: `2px solid ${BL}`, overflowX: "auto" }}>
        {STEPS.map((label, i) => (
          <button key={i} onClick={() => setStep(i)} style={{ background: "none", border: "none", borderBottom: step === i ? `2px solid ${D}` : "2px solid transparent", padding: "8px 14px", fontSize: 13, fontWeight: step === i ? 500 : 400, color: step === i ? D : "#7ab8c8", cursor: "pointer", marginBottom: -2, whiteSpace: "nowrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "50%", background: step === i ? D : (i < step ? B : BL), color: step === i ? "#fff" : (i < step ? "#fff" : "#7ab8c8"), fontSize: 11, fontWeight: 500, marginRight: 7 }}>{i < step ? "✓" : i + 1}</span>
            {label}
          </button>
        ))}
      </div>
      <div>
        <p style={{ fontSize: 18, fontWeight: 500, color: D, margin: "0 0 4px" }}>{STEPS[step]}</p>
        <div style={{ width: 32, height: 3, background: B, borderRadius: 2, marginBottom: 20 }} />
        {steps[step]}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 28, paddingTop: 20, borderTop: `1px solid ${BL}` }}>
        <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} style={{ padding: "9px 20px", borderRadius: 8, border: `1.5px solid ${step === 0 ? BL : B}`, background: "none", color: step === 0 ? BL : B, fontSize: 13, fontWeight: 500, cursor: step === 0 ? "default" : "pointer" }}>← Back</button>
        {step < STEPS.length - 1 && (
          <button onClick={() => setStep(s => s + 1)} style={{ padding: "9px 24px", borderRadius: 8, border: "none", background: step === STEPS.length - 2 ? B : D, color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
            {step === STEPS.length - 2 ? "View summary report →" : "Next →"}
          </button>
        )}
      </div>
    </div>
  );
}